import { Trans } from 'react-i18next'

import { EMPTY_OBJECT, EMPTY_STRING } from '../../../constants/common'
import { USER_STATUS_TEXT } from '../../../constants/user'
import { brToNewLine, isValueNumberType } from '../../../utils/common'
import {
  getBuildingProfilePathname,
  getFlatProfilePathname,
} from '../../../utils/link'
import {
  ENTITY_PROFILE_TAB_UPDATES_CONTENT_TRANS_PROPS,
  EntityProfileTabUpdatesCardEntityLink,
  getEntityProfileTabUpdateOwner,
} from '../../Entity'

import {
  USER_PROFILE_LOG_ACTION,
  USER_PROFILE_LOG_ACTION_TRANSLATION_KEY,
  USER_PROFILE_LOG_CONTACT_ACTION_LINK_GETTER,
  USER_PROFILE_LOG_ENTITY_LINK_ACTIONS,
  USER_PROFILE_LOG_STATUS_ACTIONS,
  USER_PROFILE_LOG_TRANSLATION_TAG_REGEXP,
} from './UserProfileTabUpdates.constants'

function getUserProfileLogStatusLabel(value, t) {
  const status = isValueNumberType(value) ? USER_STATUS_TEXT[value] : null

  if (status) {
    return t(status.text)
  }

  return value ?? EMPTY_STRING
}

function getUserProfileLogItemContent({ availableSections, item, t }) {
  if (USER_PROFILE_LOG_ENTITY_LINK_ACTIONS.includes(item?.action)) {
    return getUserProfileLogFlatBuildingContent({ availableSections, item, t })
  }

  if (USER_PROFILE_LOG_CONTACT_ACTION_LINK_GETTER[item?.action]) {
    return getUserProfileLogContactContent({ item, t })
  }

  return null
}

function getUserProfileLogContactContent({ item, t }) {
  const getContactLink =
    USER_PROFILE_LOG_CONTACT_ACTION_LINK_GETTER[item.action]

  const data = item?.data || EMPTY_OBJECT
  const prev = data.old_value
  const curr = data.new_value

  return !prev && !curr ? null : (
    <Trans
      {...ENTITY_PROFILE_TAB_UPDATES_CONTENT_TRANS_PROPS}
      components={{
        currLink: (
          <EntityProfileTabUpdatesCardEntityLink
            to={curr ? getContactLink(curr) : undefined}
          />
        ),
        prevLink: (
          <EntityProfileTabUpdatesCardEntityLink
            to={prev ? getContactLink(prev) : undefined}
          />
        ),
      }}
      i18nKey={USER_PROFILE_LOG_ACTION_TRANSLATION_KEY[item.action]}
      values={getUserProfileLogItemValues({ item, t })}
    />
  )
}

function getUserProfileLogFlatBuildingContent({ availableSections, item, t }) {
  const { building, flat } = item?.data || EMPTY_OBJECT

  if (
    flat?.id == null ||
    building?.id == null ||
    !flat.number ||
    !building.address
  ) {
    return null
  }

  return (
    <Trans
      {...ENTITY_PROFILE_TAB_UPDATES_CONTENT_TRANS_PROPS}
      components={{
        buildingLink: (
          <EntityProfileTabUpdatesCardEntityLink
            to={getBuildingProfilePathname({
              availableSections,
              id: building.id,
            })}
          />
        ),
        flatLink: (
          <EntityProfileTabUpdatesCardEntityLink
            to={getFlatProfilePathname({ availableSections, id: flat.id })}
          />
        ),
      }}
      i18nKey={USER_PROFILE_LOG_ACTION_TRANSLATION_KEY[item.action]}
      values={getUserProfileLogItemValues({ item, t })}
    />
  )
}

function hasUserProfileLogLinkedContent(action) {
  return (
    USER_PROFILE_LOG_ENTITY_LINK_ACTIONS.includes(action) ||
    Boolean(USER_PROFILE_LOG_CONTACT_ACTION_LINK_GETTER[action])
  )
}

export function getUserProfileLogItemValues({ item, t }) {
  const data = item?.data || EMPTY_OBJECT

  if (USER_PROFILE_LOG_STATUS_ACTIONS.includes(item?.action)) {
    return {
      curr: getUserProfileLogStatusLabel(data.new_value, t),
      prev: getUserProfileLogStatusLabel(data.old_value, t),
    }
  }

  if (USER_PROFILE_LOG_ENTITY_LINK_ACTIONS.includes(item?.action)) {
    return {
      building_address: data.building?.address ?? EMPTY_STRING,
      flat_number: data.flat?.number ?? EMPTY_STRING,
    }
  }

  if (item?.action === USER_PROFILE_LOG_ACTION.INVITE) {
    return {
      invite_message: brToNewLine(data.invite_message),
    }
  }

  return {
    curr: data.new_value ?? EMPTY_STRING,
    prev: data.old_value ?? EMPTY_STRING,
  }
}

export function getUserProfileLogItemText({ item, t }) {
  const translationKey = USER_PROFILE_LOG_ACTION_TRANSLATION_KEY[item?.action]

  if (!translationKey) {
    return EMPTY_STRING
  }

  const text = t(translationKey, getUserProfileLogItemValues({ item, t }))

  return hasUserProfileLogLinkedContent(item?.action)
    ? text.replace(USER_PROFILE_LOG_TRANSLATION_TAG_REGEXP, EMPTY_STRING)
    : text
}

export function toTranslatedUpdates({ availableSections, items, t }) {
  return items.map(item => ({
    ...item,
    content: getUserProfileLogItemContent({ availableSections, item, t }),
    owner_obj: getEntityProfileTabUpdateOwner(item.initiated_by),
    text: getUserProfileLogItemText({ item, t }),
    timestamp: item.event_dt,
  }))
}
